import {
  CloudOutlined,
  CodeOutlined,
  DatasetOutlined,
  DataThresholdingOutlined,
  InsertDriveFileOutlined
} from '@mui/icons-material';

import AwsIcon from '@/assets/images/exports/aws.svg';
import AzureIcon from '@/assets/images/exports/azure.svg';
import BloombergIcon from '@/assets/images/exports/bloomberg.svg';
import bloombergTerminalPreview from '@/assets/images/exports/bloombergTerminalPreview.png';
import CsvIcon from '@/assets/images/exports/csv.svg';
import EnverusIcon from '@/assets/images/exports/enverus.svg';
import enverusPreview from '@/assets/images/exports/enverusPreview.png';
import ExcelIcon from '@/assets/images/exports/excel.svg';
import excelSimplePreview from '@/assets/images/exports/excelSimplePreview.png';
import FactsetIcon from '@/assets/images/exports/factset.svg';
import GoogleCloudIcon from '@/assets/images/exports/googleCloud.svg';
import GxApiIcon from '@/assets/images/exports/gxAPI.svg';
import IceIcon from '@/assets/images/exports/ice.svg';
import JsonIcon from '@/assets/images/exports/json.svg';
import LsegIcon from '@/assets/images/exports/lseg.svg';
import lsegWorkspacePreview from '@/assets/images/exports/lsegWorkSpacePreview.png';
import NorthGravityIcon from '@/assets/images/exports/northGravity.svg';
import PowerBIIcon from '@/assets/images/exports/powerBI.svg';
import PowerQueryIcon from '@/assets/images/exports/powerQuery.svg';
import PythonIcon from '@/assets/images/exports/python.svg';
import SnowflakeIcon from '@/assets/images/exports/snowflake.svg';
import VentriksIcon from '@/assets/images/exports/ventriks.svg';
import ZemaIcon from '@/assets/images/exports/zema.svg';

export const exportOptionsGroups = [
  {
    groupName: 'File exports',
    GroupIcon: InsertDriveFileOutlined,
    options: [
      {
        id: 'csv',
        label: 'CSV',
        icon: CsvIcon,
        actionHeading: 'CSV',
        outlined: true,
        disabled: false,
        bulletPoints: [],
        previewImg: ''
      },
      {
        id: 'excel?type=default',
        label: 'Excel',
        icon: ExcelIcon,
        buttonLabel: 'Download',
        outlined: true,
        actionHeading: 'Excel',
        bulletPoints: [
          'Two tabs of data produced',
          'Summary tab for quick analysis, date only',
          'Full data including complete series with delivery timing'
        ],
        previewImg: excelSimplePreview
      }
    ]
  },
  {
    groupName: 'Cloud analytics platforms',
    GroupIcon: CloudOutlined,
    options: [
      {
        id: 'snowflake',
        icon: SnowflakeIcon,
        disabled: false,
        buttonLabel: 'Enable Access',
        actionHeading: 'GX Data in Snowflake',
        bulletPoints: [
          'Data immediately available: No ETL',
          'Tables with full metadata and history',
          'Available for all cloud providers in all regions'
        ],
        previewImg: ''
      },
      {
        id: 'aws',
        label: 'Amazon Web Services',
        icon: AwsIcon,
        buttonLabel: 'Enable Access',
        disabled: false,
        actionHeading: 'GX Data in AWS',
        bulletPoints: [
          'Data immediately available: No ETL',
          'Tables with full metadata and history',
          'Available as S3 share'
        ],
        previewImg: ''
      },
      {
        id: 'google_cloud',
        icon: GoogleCloudIcon,
        buttonLabel: 'Enable Access',
        disabled: false,
        actionHeading: 'GX Data in Google Cloud',
        bulletPoints: [
          'Data immediately available: No ETL',
          'Tables with full metadata',
          'Available via Google Cloud Storage'
        ],
        previewImg: ''
      },
      {
        id: 'azure',
        icon: AzureIcon,
        buttonLabel: 'Enable Access',
        disabled: false,
        actionHeading: 'GX Data in Azure',
        bulletPoints: [
          'Data immediately available: No ETL',
          'Tables with full metadata',
          'Blob storage via Azure Data Share'
        ],
        previewImg: ''
      },
      {
        id: 'north_gravity',
        icon: NorthGravityIcon,
        buttonLabel: 'Enable Access',
        label: 'NorthGravity',
        disabled: false,
        actionHeading: 'GX Data in NorthGravity',
        bulletPoints: [
          'Data immediately available: No ETL',
          'History, metadata, holiday calendars available',
          'Timeseries available in data preps and elsewhere'
        ],
        previewImg: ''
      }
    ]
  },
  {
    groupName: 'Business intelligence platforms',
    GroupIcon: DataThresholdingOutlined,
    options: [
      {
        id: 'power_bi',
        icon: PowerBIIcon,
        buttonLabel: 'Enable Access',
        disabled: false,
        actionHeading: 'GX Data in Power BI',
        bulletPoints: [
          'Data in Power BI dashboards.',
          'Live connection to refresh with latest data'
        ],
        previewImg: ''
      },
      {
        id: 'power_query',
        icon: PowerQueryIcon,
        buttonLabel: 'Copy to clipboard',
        label: 'Excel (PowerQuery)',
        actionHeading: 'PowerQuery (Summary Table)',
        bulletPoints: [
          'Use code in PowerBI or Excel. Data can be refreshed with Data → Refresh',
          'On PC Excel go to Data → Get Data → From Other Sources → Blank Query',
          'Click ‘Advanced Editor’; Paste code below; Click ‘Done’ → ‘Close and Load’',
          'You need an API token to export your data.'
        ],
        previewImg: '',
        disabled: false
      }
    ]
  },
  {
    groupName: 'Developer tools',
    GroupIcon: CodeOutlined,
    options: [
      {
        id: 'gx_api',
        icon: GxApiIcon,
        buttonLabel: 'Copy to clipboard',
        actionHeading: 'API',
        outlined: true,
        previewImg: '',
        bulletPoints: [
          'All symbols for the chosen codes returned',
          'You need an API token to export your data.'
        ]
      },
      {
        id: 'python',
        icon: PythonIcon,
        buttonLabel: 'Copy to clipboard',
        label: 'Python',
        actionHeading: 'Python',
        outlined: true,
        bulletPoints: [],
        previewImg: ''
      },
      {
        id: 'json',
        icon: JsonIcon,
        buttonLabel: 'Copy to clipboard',
        label: 'curl (json)',
        actionHeading: 'curl (json)',
        bulletPoints: ['You need an API token to export your data.'],
        previewImg: '',
        disabled: false
      }
    ]
  },
  {
    groupName: 'Market data integrations',
    GroupIcon: DatasetOutlined,
    options: [
      {
        id: 'bloomberg_terminal',
        icon: BloombergIcon,
        buttonLabel: 'Enable Access',
        label: 'Terminal',
        disabled: false,
        actionHeading: 'GX Data in Bloomberg Terminal',
        previewImg: bloombergTerminalPreview,
        bulletPoints: [
          'Fully integrated data available with history at SPOT GX <GO>',
          'Ticker mappings available'
        ]
      },
      {
        id: 'bloomberg_bpipe',
        icon: BloombergIcon,
        buttonLabel: 'Enable Access',
        label: 'B-PIPE',
        disabled: false,
        actionHeading: 'GX Data in Bloomberg B-PIPE',
        bulletPoints: [
          'Fully integrated data available with history',
          'EID and Ticker mappings available'
        ]
      },
      {
        id: 'bloomberg_data_license',
        icon: BloombergIcon,
        buttonLabel: 'Enable Access',
        label: 'Data License',
        disabled: false,
        actionHeading: 'GX Data in Bloomberg Data License',
        bulletPoints: [
          'Fully integrated data available with history',
          'EID and Ticker mappings available'
        ]
      },
      {
        id: 'lseg_workspace',
        icon: LsegIcon,
        buttonLabel: 'Enable Access',
        label: 'Workspace',
        disabled: false,
        actionHeading: 'GX Data in LSEG Workspace',
        bulletPoints: [
          'Fully integrated data available with history',
          'Prebuilt screens with key data',
          'RICS mappings available'
        ],
        previewImg: lsegWorkspacePreview
      },
      {
        id: 'lseg_data_scope',
        icon: LsegIcon,
        buttonLabel: 'Enable Access',
        label: 'DataScope Select',
        disabled: false,
        actionHeading: 'GX Data in LSEG DataScope Select',
        bulletPoints: ['Fully integrated data available with history', 'RICS mappings available']
      },
      {
        id: 'lseg_ldms',
        icon: LsegIcon,
        buttonLabel: 'Enable Access',
        label: 'LDMS',
        disabled: false,
        actionHeading: 'GX Data in LSEG LDMS',
        bulletPoints: ['Fully integrated data available with history', 'RICS mappings available']
      },
      {
        id: 'ice_connect',
        icon: IceIcon,
        buttonLabel: 'Enable Access',
        label: 'Connect',
        disabled: false,
        actionHeading: 'GX Data in ICE Connect',
        bulletPoints: [
          'Fully integrated data available with history',
          'Symbol and Ticker mappings available',
          'GX settled Swaps'
        ]
      },
      {
        id: 'ice_feed_products',
        icon: IceIcon,
        buttonLabel: 'Enable Access',
        label: 'Feed Products',
        disabled: false,
        actionHeading: 'GX Data in ICE Feed Products',
        bulletPoints: [
          'Fully integrated data available with history',
          'Daily and historical tickers available via Consolidated Feed'
        ]
      },
      {
        id: 'enverus',
        icon: EnverusIcon,
        buttonLabel: 'Enable Access',
        disabled: false,
        actionHeading: 'GX Data in Enverus',
        bulletPoints: [
          'Fully integrated data available with history',
          'Access GX data in Marketview, Sphere & Curvebuilder workflows'
        ],
        previewImg: enverusPreview
      },
      {
        id: 'zema',
        icon: ZemaIcon,
        buttonLabel: 'Enable Access',
        disabled: false,
        actionHeading: 'GX Data in Zema',
        bulletPoints: [
          'Fully integrated data available with history',
          'Single GX processor for all data access'
        ]
      },
      {
        id: 'fact_set',
        icon: FactsetIcon,
        buttonLabel: 'Enable Access',
        disabled: false,
        actionHeading: 'GX Data in FactSet',
        bulletPoints: ['Fully integrated data available with history']
      },
      {
        id: 'ventriks',
        icon: VentriksIcon,
        buttonLabel: 'Enable Access',
        disabled: false,
        actionHeading: 'GX Data in Ventriks',
        bulletPoints: [
          'Fully integrated data available with history',
          'Available via Data Marketplace or Data Platform'
        ]
      }
    ]
  }
];
